import { DotLottie } from "@lottiefiles/dotlottie-web";
import { useCallback, useEffect, useRef, useState } from "react";
import LottieActions from "../actions/LottieActions.js";
import { createLottieCanvas, resolveAnimationUrl } from "../utils/LottieUtils.js";

const MOUNT_DELAY_MS = 10;
const LOAD_TIMEOUT_MS = 2000;

/**
 * Manages the DotLottie instance lifecycle: creation, loading, destruction, and error handling.
 * Returns a reactive `instance` (null until loaded) so consumer effects re-fire on transitions.
 *
 * @param {Object} params
 * @param {Object} params.content - Immutable Map of Lottie content configuration.
 * @param {Object} params.elemProps - Immutable Map of element properties (dimensions).
 * @param {string} params.id - The element ID.
 * @param {string} params.layout - The current layout (desktop/mobile).
 * @returns {{ containerRef: React.RefObject, instance: DotLottie|null, isLoading: boolean }}
 */
const useLottieInstance = ({ content, elemProps, id, layout }) => {
  const [instance, setInstance] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const containerRef = useRef(null);
  /** Mirrors `instance` state for synchronous reads in destroyAnimation and the load timeout race guard. */
  const instanceRef = useRef(null);
  /** Race guard: checked by the load timeout to avoid false errors when animation loaded just in time. */
  const loadedRef = useRef(false);
  const loadTimeoutRef = useRef(null);
  const handlersRef = useRef({ load: null, loadError: null });

  /**
   * Prop-mirror refs: shadow current prop values so that loadAnimation and destroyAnimation
   * can read them without being recreated when props change. Adding these props to the callback
   * deps would cause the source-change effect to destroy/rebuild the animation on every tweak.
   */
  const contentRef = useRef(content);
  contentRef.current = content;
  const idRef = useRef(id);
  idRef.current = id;
  const layoutRef = useRef(layout);
  layoutRef.current = layout;
  const elemPropsRef = useRef(elemProps);
  elemPropsRef.current = elemProps;

  const sourceKey = content?.get("key");
  const sourceUrl = content?.get("url");
  const sourceType = content?.get("type");

  /**
   * Dispatches load error state to LottieStore.
   * Accepts elementId and elementLayout at call time to make the data flow explicit.
   *
   * @param {boolean} hasError - Whether a load error occurred.
   * @param {string} elementId - The element ID.
   * @param {string} elementLayout - The current layout (desktop/mobile).
   */
  const setLoadError = useCallback((hasError, elementId, elementLayout) => {
    LottieActions.setLoadError(elementId, elementLayout, hasError);
  }, []);

  /**
   * Destroys the current DotLottie instance and cleans up resources.
   * Uses idRef/layoutRef because this callback must remain stable (it's a dependency of the
   * source-change effect) and must read the current id/layout at destruction time.
   */
  const destroyAnimation = useCallback(() => {
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }

    loadedRef.current = false;
    setInstance(null);
    setIsLoading(true);
    setLoadError(false, idRef.current, layoutRef.current);

    const activeInstance = instanceRef.current;
    if (activeInstance) {
      if (typeof activeInstance.removeEventListener === "function") {
        const { load, loadError } = handlersRef.current;
        if (load) activeInstance.removeEventListener("load", load);
        if (loadError) activeInstance.removeEventListener("loadError", loadError);
      }
      handlersRef.current = { load: null, loadError: null };

      if (typeof activeInstance.destroy === "function") {
        activeInstance.destroy();
      }
      instanceRef.current = null;
    }

    if (containerRef.current) {
      containerRef.current.innerHTML = "";
    }

    LottieActions.cleanup(idRef.current, layoutRef.current);
  }, [setLoadError]);

  const loadAnimation = useCallback(() => {
    const currentContent = contentRef.current;
    if (!currentContent) return;

    const type = currentContent.get("type");
    if (!type) return;

    let animationDataUrl = resolveAnimationUrl(currentContent);
    if (!animationDataUrl) return;

    try {
      if (!containerRef.current) return;

      if (animationDataUrl.startsWith("//")) {
        animationDataUrl = `https:${animationDataUrl}`;
      }

      const currentElemProps = elemPropsRef.current;
      const w = currentElemProps?.get("w", 0) ?? 0;
      const h = currentElemProps?.get("h", 0) ?? 0;
      const elementId =
        idRef.current || `lottie-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

      const canvas = createLottieCanvas(elementId, w, h, containerRef.current.ownerDocument);
      containerRef.current.innerHTML = "";
      containerRef.current.appendChild(canvas);

      const dotLottieConfig = {
        canvas,
        src: animationDataUrl,
        autoplay: false,
      };

      const newPlayer = new DotLottie(dotLottieConfig);
      instanceRef.current = newPlayer;

      const loadHandler = () => {
        if (!newPlayer.totalFrames || newPlayer.totalFrames === 0) {
          setLoadError(true, idRef.current, layoutRef.current);
          return;
        }
        loadedRef.current = true;
        setInstance(newPlayer);
        setIsLoading(false);
        setLoadError(false, idRef.current, layoutRef.current);
      };

      const errorHandler = (event) => {
        console.error("Lottie animation load error:", event?.error || event);
        setLoadError(true, idRef.current, layoutRef.current);
      };

      handlersRef.current = { load: loadHandler, loadError: errorHandler };
      newPlayer.addEventListener("load", loadHandler);
      newPlayer.addEventListener("loadError", errorHandler);

      loadTimeoutRef.current = setTimeout(() => {
        if (!loadedRef.current && instanceRef.current === newPlayer) {
          setLoadError(true, idRef.current, layoutRef.current);
        }
      }, LOAD_TIMEOUT_MS);
    } catch (error) {
      console.error("Failed to load Lottie animation:", error);
      setLoadError(true, idRef.current, layoutRef.current);
    }
  }, [setLoadError]);

  /** Load animation on mount and reload when the source (key/url/type) changes. */
  // biome-ignore lint: sourceKey, sourceUrl, sourceType are change-detection deps that trigger reload
  useEffect(() => {
    const timeout = setTimeout(loadAnimation, MOUNT_DELAY_MS);
    return () => {
      clearTimeout(timeout);
      destroyAnimation();
    };
  }, [sourceKey, sourceUrl, sourceType, loadAnimation, destroyAnimation]);

  return { containerRef, instance, isLoading };
};

export default useLottieInstance;
